"use client"

import { useState, useEffect } from "react"
import { ArrowLeft, Plus, X, ChevronDown, ChevronUp } from "lucide-react"
import PlanningMapView from "./PlanningMapView"
import "./PlanningInterface.css"

// Update your component props (line 10)
const PlanningInterface = ({
  places,
  currentListId,
  userId,
  userNickname,
  onBackToMap,
  onBackToVoting,
  API_BASE,
  linkedTripData,
  onTripDataUpdate,
  placeTypes = {},
  myVotes = {}, // ADD THIS LINE
}) => {
  // Load trip data from props or localStorage
  const [tripData, setTripData] = useState(linkedTripData)
  const [selectedDate, setSelectedDate] = useState(null)
  const [currentMode, setCurrentMode] = useState("view")
  const [visiblePlaces, setVisiblePlaces] = useState(places)

  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [showAccommodationModal, setShowAccommodationModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportData, setExportData] = useState("")
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  // Add this near your other useState declarations (around line 20)
  const [showPlannedOnly, setShowPlannedOnly] = useState(false)

  // Edit states
  const [currentEditingPlanInfo, setCurrentEditingPlanInfo] = useState({
    originalDate: null,
    dayIndex: -1,
    planIndex: -1,
  })
  const [currentEditingAccommodationId, setCurrentEditingAccommodationId] = useState(null)

  // Sharing state - matching your API structure
  const [isSharedPlan, setIsSharedPlan] = useState(false)
  const [sharedPlanId, setSharedPlanId] = useState("s1z739qq") // This is the master_plan_id
  const [currentActiveLinkId, setCurrentActiveLinkId] = useState("31ztxnoi") // This is the link_id
  const [canEdit, setCanEdit] = useState(true)

  // Map collapse state
  const [isMapCollapsed, setIsMapCollapsed] = useState(false)

  const PLAN_ICONS = {
    accommodation: "🏠",
    meal: "🍽️",
    activity: "🎯",
    flight: "✈️",
    drive: "🚗",
    note: "📝",
  }

  // Load trip data when component mounts or linkedTripData changes
  useEffect(() => {
    if (linkedTripData) {
      console.log("📅 Loading linked trip data:", linkedTripData)
      setTripData(linkedTripData)
      //setSharedPlanId(linkedTripData.sharedPlanId || null)
      //setCurrentActiveLinkId(linkedTripData.currentActiveLinkId || null)
      setIsSharedPlan(!!linkedTripData.sharedPlanId)

      // Set initial selected date - but preserve current selection if it's still valid
      if (linkedTripData.itinerary && linkedTripData.itinerary.length > 0) {
        // Check if current selectedDate is still valid in the updated data
        const currentDateStillExists = selectedDate && linkedTripData.itinerary.find((day) => day.date === selectedDate)

        if (currentDateStillExists) {
          // Keep the current selectedDate since it's still valid
          console.log("📅 Preserving current selected date:", selectedDate)
        } else {
          // Only set a new selectedDate if current one is invalid or not set
          const today = new Date().toISOString().split("T")[0]
          const todayInTrip = linkedTripData.itinerary.find((day) => day.date === today)
          const newSelectedDate = todayInTrip ? today : linkedTripData.itinerary[0].date
          console.log("📅 Setting new selected date:", newSelectedDate)
          setSelectedDate(newSelectedDate)
        }
      }
    } else {
      // Try to load from localStorage as fallback
      loadTripDataFromStorage()
    }
  }, [linkedTripData])

  // Update visible places when places prop changes
  useEffect(() => {
    setVisiblePlaces(places)
  }, [places])

  const loadTripDataFromStorage = () => {
    try {
      const data = localStorage.getItem("tripCalendarData_v3")
      if (data) {
        const loaded = JSON.parse(data)
        console.log("📅 Loaded trip data from localStorage:", loaded)
        setTripData(loaded)
        // setSharedPlanId(loaded.sharedPlanId || null)
        // setCurrentActiveLinkId(loaded.currentActiveLinkId || null)
        setIsSharedPlan(!!loaded.sharedPlanId)

        // Set initial selected date - but preserve current selection if it's still valid
        if (loaded.itinerary && loaded.itinerary.length > 0) {
          // Check if current selectedDate is still valid in the loaded data
          const currentDateStillExists = selectedDate && loaded.itinerary.find((day) => day.date === selectedDate)

          if (!currentDateStillExists) {
            // Only set a new selectedDate if current one is invalid or not set
            const today = new Date().toISOString().split("T")[0]
            const todayInTrip = loaded.itinerary.find((day) => day.date === today)
            setSelectedDate(todayInTrip ? today : loaded.itinerary[0].date)
          }
        }
      }
    } catch (error) {
      console.error("Error loading trip data from localStorage:", error)
    }
  }

  // In your existing code, add this filter
  // Filter places based on what's planned for the selected date
  const getPlannedPlacesForDate = () => {
    if (!tripData || !selectedDate) return []

    const selectedDay = tripData.itinerary.find((day) => day.date === selectedDate)
    if (!selectedDay || !selectedDay.plans) return []

    // Get all activity plans for this date
    const activityPlans = selectedDay.plans.filter((plan) => plan.type === "activity")

    // Extract place names from activity details and match with places
    const plannedPlaces = places.filter((place) => {
      return activityPlans.some(
        (plan) =>
          plan.details.toLowerCase().includes(place.name.toLowerCase()) ||
          place.name.toLowerCase().includes(plan.details.toLowerCase().replace("visit ", "")),
      )
    })

    return plannedPlaces
  }

  const plannedPlacesForDate = getPlannedPlacesForDate()

  // Auto-switch back to "All" view if no planned places for this date
  const shouldShowPlannedOnly = showPlannedOnly && plannedPlacesForDate.length > 0
  const displayPlaces = shouldShowPlannedOnly ? plannedPlacesForDate : places

  // Save trip data - matching your API structure exactly
  const saveToDatabase = async (updatedTripData) => {
    if (isSharedPlan && sharedPlanId && canEdit && API_BASE && userId) {
      console.log("Attempting to save to database. Conditions met:", {
        isSharedPlan,
        sharedPlanId,
        canEdit,
        userId,
        currentActiveLinkId,
        calendarName: updatedTripData.calendarName,
        itineraryLength: updatedTripData.itinerary?.length || 0,
        accommodationsLength: updatedTripData.accommodations?.length || 0,
      })

      try {
        // Use the exact parameter names your API expects
        const response = await fetch(`${API_BASE}/plan-manager.php?action=update`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            masterPlanId: sharedPlanId, // Your API expects masterPlanId
            userId: userId, // Your API expects userId
            calendarName: updatedTripData.calendarName, // Your API expects calendarName
            itinerary: updatedTripData.itinerary, // Your API expects itinerary
            accommodations: updatedTripData.accommodations || [], // Your API expects accommodations
            tripTimezone: updatedTripData.tripTimezone, // Your API expects tripTimezone
            currentLinkId: currentActiveLinkId, // Your API expects currentLinkId
          }),
        })

        const data = await response.json()
        if (data.success) {
          console.log("✅ Changes saved to server!")
        } else {
          throw new Error(data.error || "Failed to save to server")
        }
      } catch (error) {
        console.error("saveToDatabase caught an error:", error)
        // Don't throw - just log the error like your previous code
      }
    } else {
      console.log("Conditions not met for saving to database:", {
        isSharedPlan,
        sharedPlanId,
        canEdit,
        hasAPI: !!API_BASE,
        hasUserId: !!userId,
      })
    }
  }

  // Save trip data to localStorage and database
  const saveTripData = async (updatedTripData) => {
    try {
      // Always save to localStorage first
      localStorage.setItem("tripCalendarData_v3", JSON.stringify(updatedTripData))
      console.log("✅ Trip data saved to localStorage")

      // Update local state
      setTripData(updatedTripData)

      // Save to database if it's a shared plan
      await saveToDatabase(updatedTripData)

      // Notify parent component to save linked data
      if (onTripDataUpdate) {
        await onTripDataUpdate(updatedTripData)
      }
    } catch (error) {
      console.error("❌ Error saving trip data:", error)
    }
  }

  // Utility functions
  const formatDateForInput = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const generateId = () => {
    return "id_" + Math.random().toString(36).substring(2, 15)
  }

  // Get accommodation class for styling - dynamic color assignment
  const getAccommodationClass = (day) => {
    if (!day.plans) return "no-accommodation"

    const accommodation = day.plans.find((plan) => plan.type === "accommodation")
    if (!accommodation) return "no-accommodation"

    // Create a simple hash from the accommodation name to assign consistent colors
    const accommodationName = accommodation.details.toLowerCase().trim()

    // Simple hash function to convert string to number
    let hash = 0
    for (let i = 0; i < accommodationName.length; i++) {
      const char = accommodationName.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32-bit integer
    }

    // Use absolute value and modulo to get a color index (0-5)
    const colorIndex = Math.abs(hash) % 6

    // Return the appropriate color class
    switch (colorIndex) {
      case 0:
        return "location-color-1"
      case 1:
        return "location-color-2"
      case 2:
        return "location-color-3"
      case 3:
        return "location-color-4"
      case 4:
        return "location-color-5"
      case 5:
        return "location-color-6"
      default:
        return "location-color-1"
    }
  }

  // Check if date is today
  const isToday = (dateStr) => {
    const today = new Date().toISOString().split("T")[0]
    return dateStr === today
  }

  // Handle planning places (called from map)
  const handlePlanPlaces = (placesToPlan, targetDate) => {
    console.log("🎯 handlePlanPlaces called!", {
      placesToPlanCount: placesToPlan.length,
      targetDate,
      tripData: !!tripData,
    })

    const dateToUse = targetDate || selectedDate

    if (!tripData || !dateToUse || placesToPlan.length === 0) {
      console.log("❌ Cannot plan places: missing data")
      alert("Please select a date and ensure there are places to plan.")
      return
    }

    // Create a deep copy to ensure state updates
    const updatedTripData = JSON.parse(JSON.stringify(tripData))
    const dayIndex = updatedTripData.itinerary.findIndex((day) => day.date === dateToUse)

    if (dayIndex === -1) {
      console.log("❌ Selected date not found in itinerary")
      alert("Selected date not found in trip itinerary.")
      return
    }

    let addedCount = 0

    // Add each place as an activity
    placesToPlan.forEach((place, index) => {
      const newActivity = {
        type: "activity",
        details: `Visit ${place.name}`,
        time: `${String(10 + index).padStart(2, "0")}:00`,
        confirmed: false,
      }

      // Check if this activity already exists
      const existingActivity = updatedTripData.itinerary[dayIndex].plans.find(
        (plan) => plan.type === "activity" && plan.details === newActivity.details,
      )

      if (!existingActivity) {
        updatedTripData.itinerary[dayIndex].plans.push(newActivity)
        addedCount++
      }
    })

    // Sort plans by time
    updatedTripData.itinerary[dayIndex].plans.sort((a, b) => {
      if (a.type === "accommodation" && b.type !== "accommodation") return -1
      if (a.type !== "accommodation" && b.type === "accommodation") return 1
      return (a.time || "23:59").localeCompare(b.time || "23:59")
    })

    saveTripData(updatedTripData)

    if (addedCount > 0) {
      alert(`✅ Added ${addedCount} new activities to ${formatDateForDisplay(dateToUse).fullDate}!`)
    } else {
      alert(`ℹ️ All ${placesToPlan.length} places were already planned for this day.`)
    }
  }

  // Add/Edit plan
  const savePlan = (planData) => {
    console.log("💾 Saving plan:", planData)
    const { dayIndex, planIndex } = currentEditingPlanInfo
    const { newDate, type, time, details, confirmed } = planData

    if (!newDate || !type || !details) {
      alert("Date, Type, and Details are required.")
      return
    }

    const plan = { type, time, details, confirmed }
    const updatedTripData = JSON.parse(JSON.stringify(tripData))

    if (planIndex === -1) {
      // Adding new plan
      const targetDayIndex = updatedTripData.itinerary.findIndex((d) => d.date === newDate)
      if (targetDayIndex === -1) {
        alert("Cannot add plan to a non-existent day.")
        return
      }

      updatedTripData.itinerary[targetDayIndex].plans.push(plan)
      updatedTripData.itinerary[targetDayIndex].plans.sort((a, b) => {
        if (a.type === "accommodation" && b.type !== "accommodation") return -1
        if (a.type !== "accommodation" && b.type === "accommodation") return 1
        return (a.time || "23:59").localeCompare(b.time || "23:59")
      })
    } else {
      // Editing existing plan
      updatedTripData.itinerary[dayIndex].plans[planIndex] = plan
      updatedTripData.itinerary[dayIndex].plans.sort((a, b) => {
        if (a.type === "accommodation" && b.type !== "accommodation") return -1
        if (a.type !== "accommodation" && b.type === "accommodation") return 1
        return (a.time || "23:59").localeCompare(b.time || "23:59")
      })
    }

    saveTripData(updatedTripData)
    setShowPlanModal(false)
    setCurrentEditingPlanInfo({ originalDate: null, dayIndex: -1, planIndex: -1 })
  }

  // Delete plan
  const deletePlan = () => {
    setConfirmAction(() => async () => {
      const { dayIndex, planIndex } = currentEditingPlanInfo
      const updatedTripData = JSON.parse(JSON.stringify(tripData))
      updatedTripData.itinerary[dayIndex].plans.splice(planIndex, 1)
      saveTripData(updatedTripData)
      setShowPlanModal(false)
      setCurrentEditingPlanInfo({ originalDate: null, dayIndex: -1, planIndex: -1 })
      setShowConfirmDialog(false)
    })
    setShowConfirmDialog(true)
  }

  // Open plan modal for editing
  const openPlanModal = (dayIndex = -1, planIndex = -1, date = selectedDate) => {
    setCurrentEditingPlanInfo({
      originalDate: date,
      dayIndex,
      planIndex,
    })
    setShowPlanModal(true)
  }

  // Handle map bounds change
  const handleMapBoundsChange = (newVisiblePlaces) => {
    setVisiblePlaces(newVisiblePlaces)
  }

  const formatDateForDisplay = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00")
    return {
      weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
      day: date.getDate(),
      fullDate: date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      }),
      monthDay: date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      }),
      fullDateWithYear: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      year: date.getFullYear(),
    }
  }

  // Handle export with database sync through parent component
  const handleExport = async () => {
    try {
      setIsLoading(true)

      const data = {
        calendarName: tripData.calendarName,
        itinerary: tripData.itinerary,
        accommodations: tripData.accommodations || [],
        tripTimezone: tripData.tripTimezone,
        sharedPlanId,
        currentActiveLinkId,
      }

      // Sync to database first
      await saveToDatabase(data)

      const dataStr = JSON.stringify(data, null, 2)
      setExportData(dataStr)
      setShowExportModal(true)
    } catch (error) {
      console.error("❌ Export error:", error)
      alert("❌ Error exporting trip data: " + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle import with database sync - FIXED
  const handleImportFromText = async (importText) => {
    try {
      setIsLoading(true)
      const importedData = JSON.parse(importText)

      if (!importedData.calendarName || !Array.isArray(importedData.itinerary)) {
        throw new Error("Invalid trip data format")
      }

      // Update trip data
      const updatedTripData = {
        calendarName: importedData.calendarName,
        itinerary: importedData.itinerary,
        accommodations: importedData.accommodations || [],
        tripTimezone: importedData.tripTimezone || "America/Chicago",
        // sharedPlanId: importedData.sharedPlanId || null,
        // currentActiveLinkId: importedData.currentActiveLinkId || null,
      }

      // Update local state first
      setSharedPlanId(updatedTripData.sharedPlanId)
      setCurrentActiveLinkId(updatedTripData.currentActiveLinkId)
      setIsSharedPlan(!!updatedTripData.sharedPlanId)

      // Save to database and localStorage
      await saveTripData(updatedTripData)

      // Set initial selected date
      if (updatedTripData.itinerary.length > 0) {
        const today = new Date().toISOString().split("T")[0]
        const todayInTrip = updatedTripData.itinerary.find((day) => day.date === today)
        setSelectedDate(todayInTrip ? today : updatedTripData.itinerary[0].date)
      }

      // Close the import modal
      setShowImportModal(false)

      // Switch to edit mode so user can immediately interact with their imported data
      setCurrentMode("edit")

      alert("✅ Trip data imported successfully!")
    } catch (error) {
      console.error("Import error:", error)
      alert("❌ Error importing trip data: " + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle creating/updating shared plan - matching your API structure exactly
  const handleCreateSharedPlan = async () => {
    if (!API_BASE || !userId) {
      alert("❌ Sharing not available - missing API configuration")
      return null
    }

    try {
      setIsLoading(true)

      const tripDataToShare = {
        calendarName: tripData.calendarName,
        itinerary: tripData.itinerary,
        accommodations: tripData.accommodations || [],
        tripTimezone: tripData.tripTimezone,
      }

      // Use the exact parameter names your API expects
      const response = await fetch(`${API_BASE}/plan-manager.php?action=share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          creatorUserId: userId, // Your API expects creatorUserId
          calendarName: tripDataToShare.calendarName, // Your API expects calendarName
          itinerary: tripDataToShare.itinerary, // Your API expects itinerary
          accommodations: tripDataToShare.accommodations, // Your API expects accommodations
          tripTimezone: tripDataToShare.tripTimezone, // Your API expects tripTimezone
          accessType: "edit", // Your API expects accessType
          existingMasterPlanId: sharedPlanId, // Your API expects existingMasterPlanId
        }),
      })

      const result = await response.json()

      if (result.success) {
        // Update local state with new shared plan info
        setSharedPlanId(result.master_plan_id) // Your API returns master_plan_id
        setCurrentActiveLinkId(result.link_id) // Your API returns link_id
        setIsSharedPlan(true)

        // Update trip data with sharing info
        const updatedTripData = {
          ...tripDataToShare,
          sharedPlanId: result.master_plan_id,
          currentActiveLinkId: result.link_id,
        }

        // Save updated data locally
        localStorage.setItem("tripCalendarData_v3", JSON.stringify(updatedTripData))
        setTripData(updatedTripData)

        return {
          planId: result.master_plan_id,
          linkId: result.link_id,
          shareUrl: result.url, // Your API returns url
        }
      } else {
        throw new Error(result.error || "Failed to create shared plan")
      }
    } catch (error) {
      console.error("❌ Error creating shared plan:", error)
      alert("❌ Error creating shared plan: " + error.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  // Handle clear all - COMPLETELY CLEAR like your previous code
  const handleClearAll = () => {
    setConfirmAction(() => async () => {
      try {
        setIsLoading(true)

        // COMPLETELY clear everything - reset to empty state like your previous code
        const clearedTripData = {
          calendarName: "Trip Calendar",
          itinerary: [], // EMPTY array - no dates at all
          accommodations: [],
          tripTimezone: "America/Chicago",
          sharedPlanId: sharedPlanId, // Keep sharing info
          currentActiveLinkId: currentActiveLinkId,
        }

        // Save the completely cleared data
        await saveTripData(clearedTripData)

        // Reset selected date since there are no dates now
        setSelectedDate(null)

        alert("✅ All trip data cleared successfully!")
        setShowConfirmDialog(false)
      } catch (error) {
        console.error("❌ Error clearing all data:", error)
        alert("❌ Error clearing all data: " + error.message)
      } finally {
        setIsLoading(false)
      }
    })
    setShowConfirmDialog(true)
  }

  // Handle import - show modal instead of file picker
  const handleImport = () => {
    setShowImportModal(true)
  }

  // Helper function to get map header text
  const getMapHeaderText = () => {
    if (!selectedDate) {
      return {
        title: "All Locations",
        subtitle: `${visiblePlaces.length} location${visiblePlaces.length !== 1 ? "s" : ""} visible`,
      }
    }

    const dateInfo = formatDateForDisplay(selectedDate)

    if (shouldShowPlannedOnly) {
      return {
        title: `Planned for ${dateInfo.monthDay}`,
        subtitle: `${plannedPlacesForDate.length} planned location${plannedPlacesForDate.length !== 1 ? "s" : ""}`,
      }
    } else {
      return {
        title: "All Locations",
        subtitle: `${visiblePlaces.length} location${visiblePlaces.length !== 1 ? "s" : ""} visible`,
      }
    }
  }

  if (!tripData || !tripData.itinerary || tripData.itinerary.length === 0) {
    return (
      <div className="planning-interface">
        <div className="planning-header">
          <button onClick={onBackToMap} className="back-btn">
            <ArrowLeft size={24} />
          </button>
          <h1>No Trip Data</h1>
        </div>
        <div className="no-trip-message">
          <p>No trip data found. Please create or import a trip first using the calendar button.</p>
        </div>
      </div>
    )
  }

  const selectedDay = tripData.itinerary.find((day) => day.date === selectedDate)
  const mapHeaderInfo = getMapHeaderText()

  return (
    <div className="planning-interface">
      {/* Header */}
      <div className="planning-header">
        <button onClick={onBackToMap} className="back-btn">
          <ArrowLeft size={24} />
        </button>
        <h1 className="trip-title">{tripData.calendarName || "Trip Plan"}</h1>
        <div className="header-actions">
          <div className="view-edit-toggle">
            <button
              className={`toggle-btn ${currentMode === "view" ? "active" : ""}`}
              onClick={() => setCurrentMode("view")}
            >
              View
            </button>
            <button
              className={`toggle-btn ${currentMode === "edit" ? "active" : ""}`}
              onClick={() => setCurrentMode("edit")}
              disabled={!canEdit}
            >
              Edit
            </button>
          </div>
        </div>
      </div>

      {/* Edit Carousel - only show in edit mode */}
      {currentMode === "edit" && (
        <div className="edit-carousel">
          <button className="edit-action-btn export" onClick={handleExport} disabled={isLoading}>
            📤 Export
          </button>
          <button className="edit-action-btn import" onClick={handleImport} disabled={isLoading}>
            👥 Import
          </button>
          <button className="edit-action-btn share" onClick={() => setShowShareModal(true)} disabled={isLoading}>
            🔗 Share
          </button>
          <button className="edit-action-btn clear-all" onClick={handleClearAll} disabled={isLoading}>
            🗑️ Clear All
          </button>
        </div>
      )}

      {/* Enhanced Date Carousel with accommodation styling */}
      <div className="date-carousel">
        {tripData.itinerary.map((day) => {
          const dateInfo = formatDateForDisplay(day.date)
          const isSelected = selectedDate === day.date
          const accommodationClass = getAccommodationClass(day)
          const todayClass = isToday(day.date) ? "is-today" : ""

          return (
            <button
              key={day.date}
              className={`date-item ${accommodationClass} ${isSelected ? "selected" : ""} ${todayClass}`}
              onClick={() => setSelectedDate(day.date)}
            >
              <div className="date-weekday">{dateInfo.weekday.toUpperCase()}</div>
              <div className="date-number">{dateInfo.day}</div>
              <div className={`date-indicator ${accommodationClass}`}></div>
            </button>
          )
        })}
      </div>

      {/* Scrollable Content Container */}
      <div className="scrollable-content">
        {/* Collapsible Map Section */}
        <div className={`map-section-container ${isMapCollapsed ? "collapsed" : ""}`}>
          <button className="map-accordion-header" onClick={() => setIsMapCollapsed(!isMapCollapsed)}>
            <div className="map-accordion-title">
              <span className="map-icon">🗺️</span>
              <div className="map-header-text">
                <span className="map-title">{mapHeaderInfo.title}</span>
                <span className="map-subtitle">{mapHeaderInfo.subtitle}</span>
              </div>
            </div>
            <div className="map-header-controls">
              {plannedPlacesForDate.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation() // Prevent accordion toggle
                    setShowPlannedOnly(!showPlannedOnly)
                  }}
                  className={`map-view-toggle ${shouldShowPlannedOnly ? "active" : ""}`}
                >
                  {shouldShowPlannedOnly ? "See All" : "See Planned"}
                </button>
              )}
              <div className="map-accordion-chevron">
                {isMapCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
              </div>
            </div>
          </button>
          {!isMapCollapsed && (
            <div className="map-section">
              <PlanningMapView
                places={displayPlaces} // Pass filtered places here
                onBoundsChange={handleMapBoundsChange}
                onPlanPlaces={handlePlanPlaces}
                selectedDate={selectedDate}
                placeTypes={placeTypes}
                showPlannedOnly={shouldShowPlannedOnly} // Use the corrected state
              />
            </div>
          )}
        </div>

        {/* Day Details */}
        {selectedDay && (
          <div className="day-details">
            <div className="day-header">
              <h2 className="day-title">{formatDateForDisplay(selectedDay.date).monthDay}</h2>
              <span className="day-subtitle">{formatDateForDisplay(selectedDay.date).fullDateWithYear}</span>
            </div>

            <div className="plans-list">
              {selectedDay.plans && selectedDay.plans.length > 0 ? (
                selectedDay.plans.map((plan, index) => {
                  // Get the accommodation class for this day to apply matching colors
                  const accommodationClass = plan.type === "accommodation" ? getAccommodationClass(selectedDay) : ""

                  return (
                    <div
                      key={index}
                      className={`plan-item ${plan.type} ${accommodationClass}`}
                      onClick={() => {
                        if (currentMode === "edit") {
                          openPlanModal(
                            tripData.itinerary.findIndex((d) => d.date === selectedDate),
                            index,
                            selectedDate,
                          )
                        }
                      }}
                      style={{ cursor: currentMode === "edit" ? "pointer" : "default" }}
                    >
                      <div className="plan-icon">{PLAN_ICONS[plan.type] || "📌"}</div>
                      <div className="plan-content">
                        <div className="plan-header">
                          <span className="plan-time">{plan.time || "All Day"}</span>
                          {plan.confirmed && <span className="plan-status confirmed">✅ Confirmed</span>}
                        </div>
                        <h3 className="plan-title">{plan.details}</h3>
                        <p className="plan-type">{plan.type.charAt(0).toUpperCase() + plan.type.slice(1)}</p>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="no-plans">
                  <p>No plans for this day yet.</p>
                  {currentMode === "edit" && (
                    <button className="add-plan-btn" onClick={() => openPlanModal(-1, -1, selectedDate)}>
                      <Plus size={16} />
                      Add your first plan
                    </button>
                  )}
                </div>
              )}

              {currentMode === "edit" && selectedDay.plans.length > 0 && (
                <div className="add-plan-buttons">
                  <button className="btn btn-outline" onClick={() => openPlanModal(-1, -1, selectedDate)}>
                    <Plus size={16} />
                    Add Plan
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showPlanModal && <PlanModal />}
      {showShareModal && <ShareModal />}
      {showImportModal && <ImportModal />}
      {showConfirmDialog && <ConfirmDialog />}
      {showExportModal && <ExportModal />}
    </div>
  )

  // Plan Modal Component
  function PlanModal() {
    const { dayIndex, planIndex } = currentEditingPlanInfo
    const isEditing = planIndex !== -1
    const existingPlan = isEditing ? tripData.itinerary[dayIndex]?.plans[planIndex] : null

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>{isEditing ? "Edit Plan" : "Add Plan"}</h3>
            <button onClick={() => setShowPlanModal(false)} className="close-btn">
              <X size={20} />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const formData = new FormData(e.target)
              savePlan({
                newDate: formData.get("date"),
                type: formData.get("type"),
                time: formData.get("time"),
                details: formData.get("details"),
                confirmed: formData.get("confirmed") === "on",
              })
            }}
          >
            <div className="modal-content">
              <div className="form-group">
                <label>Date:</label>
                <select name="date" required defaultValue={selectedDate}>
                  {tripData.itinerary.map((day) => (
                    <option key={day.date} value={day.date}>
                      {day.displayDate || formatDateForDisplay(day.date).fullDate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type:</label>
                  <select name="type" required defaultValue={existingPlan?.type || "activity"}>
                    <option value="accommodation">🏠 Accommodation</option>
                    <option value="meal">🍽️ Meal</option>
                    <option value="activity">🎯 Activity</option>
                    <option value="flight">✈️ Flight</option>
                    <option value="drive">🚗 Drive</option>
                    <option value="note">📝 Note</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Time:</label>
                  <input type="time" name="time" defaultValue={existingPlan?.time || "10:00"} />
                </div>
              </div>
              <div className="form-group">
                <label>Details:</label>
                <textarea
                  name="details"
                  required
                  rows={3}
                  placeholder="Enter plan details..."
                  defaultValue={existingPlan?.details || ""}
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" name="confirmed" defaultChecked={existingPlan?.confirmed || false} />
                  Confirmed
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <div className="modal-footer-left">
                {isEditing && (
                  <button type="button" onClick={deletePlan} className="btn btn-danger">
                    Delete
                  </button>
                )}
              </div>
              <div className="modal-footer-right">
                <button type="button" onClick={() => setShowPlanModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {isEditing ? "Update" : "Add"} Plan
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Share Modal Component - Enhanced with database functionality
  function ShareModal() {
    const [exportData, setExportData] = useState("")
    const [showExportData, setShowExportData] = useState(false)
    const [shareUrl, setShareUrl] = useState("")
    const [showShareUrl, setShowShareUrl] = useState(false)

    const generateExportData = async () => {
      try {
        setIsLoading(true)

        const data = {
          calendarName: tripData.calendarName,
          itinerary: tripData.itinerary,
          accommodations: tripData.accommodations || [],
          tripTimezone: tripData.tripTimezone,
          sharedPlanId,
          currentActiveLinkId,
        }

        // Sync to database first
        await saveToDatabase(data)

        const dataStr = JSON.stringify(data, null, 2)
        setExportData(dataStr)
        setShowExportData(true)
      } catch (error) {
        console.error("❌ Error generating export data:", error)
        alert("❌ Error generating export data: " + error.message)
      } finally {
        setIsLoading(false)
      }
    }

    const generateShareLink = async () => {
      try {
        const shareInfo = await handleCreateSharedPlan()
        if (shareInfo) {
          setShareUrl(shareInfo.shareUrl)
          setShowShareUrl(true)
        }
      } catch (error) {
        console.error("❌ Error generating share link:", error)
        alert("❌ Error generating share link: " + error.message)
      }
    }

    const copyToClipboard = async (text, successMessage) => {
      try {
        await navigator.clipboard.writeText(text)
        alert(successMessage)
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea")
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
        alert(successMessage)
      }
    }

    if (showExportData) {
      return (
        <div className="modal-overlay">
          <div className="modal export-modal">
            <div className="modal-header">
              <h3>Export Data</h3>
              <button onClick={() => setShowShareModal(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content export-content">
              <textarea value={exportData} readOnly className="export-textarea" rows={15} />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowExportData(false)} className="btn btn-secondary">
                Back
              </button>
              <button
                onClick={() => copyToClipboard(exportData, "✅ Trip data copied to clipboard!")}
                className="btn btn-primary"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )
    }

    if (showShareUrl) {
      return (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Share Link Created</h3>
              <button onClick={() => setShowShareModal(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="share-url-container">
                <p>Your trip has been saved and can be shared with this link:</p>
                <div className="share-url-box">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="share-url-input"
                    onClick={(e) => e.target.select()}
                  />
                </div>
                <p className="help-text">Anyone with this link can view and collaborate on your trip plan.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowShareUrl(false)} className="btn btn-secondary">
                Back
              </button>
              <button
                onClick={() => copyToClipboard(shareUrl, "✅ Share link copied to clipboard!")}
                className="btn btn-primary"
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>Share & Export</h3>
            <button onClick={() => setShowShareModal(false)} className="close-btn">
              <X size={20} />
            </button>
          </div>
          <div className="modal-content">
            <div className="share-options">
              <div className="share-option">
                <button onClick={generateShareLink} className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? "Creating..." : "🔗 Create Share Link"}
                </button>
                <p className="help-text">
                  Create a shareable link that others can use to view and collaborate on your trip.
                </p>
              </div>

              <div className="share-divider">
                <span>or</span>
              </div>

              <div className="share-option">
                <button onClick={generateExportData} className="btn btn-secondary" disabled={isLoading}>
                  {isLoading ? "Exporting..." : "📤 Export Data"}
                </button>
                <p className="help-text">Export your trip data to copy and paste elsewhere or import later.</p>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={() => setShowShareModal(false)} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Import Modal Component - FIXED
  function ImportModal() {
    const [importText, setImportText] = useState("")

    return (
      <div className="modal-overlay">
        <div className="modal import-modal">
          <div className="modal-header">
            <h3>Import Data</h3>
            <button onClick={() => setShowImportModal(false)} className="close-btn">
              <X size={20} />
            </button>
          </div>
          <div className="modal-content import-content">
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="Paste your exported trip data here..."
              className="import-textarea"
              rows={15}
            />
          </div>
          <div className="modal-footer">
            <button onClick={() => setShowImportModal(false)} className="btn btn-secondary">
              Close
            </button>
            <button
              onClick={() => handleImportFromText(importText)}
              className="btn btn-primary"
              disabled={!importText.trim() || isLoading}
            >
              {isLoading ? "Importing..." : "Import"}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Confirm Dialog Component
  function ConfirmDialog() {
    if (!showConfirmDialog) return null

    const isDeletePlan = currentEditingPlanInfo.planIndex !== -1
    const title = isDeletePlan ? "Confirm Delete" : "Confirm Clear All"
    const message = isDeletePlan
      ? "Are you sure you want to delete this plan?"
      : "Are you sure you want to clear ALL trip data? This will remove all dates, plans, and accommodations. This action cannot be undone."

    return (
      <div className="modal-overlay">
        <div className="modal confirm-modal">
          <div className="modal-header">
            <h3>{title}</h3>
          </div>
          <div className="modal-content">
            <p>{message}</p>
          </div>
          <div className="modal-footer">
            <button onClick={() => setShowConfirmDialog(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button onClick={confirmAction} className="btn btn-danger" disabled={isLoading}>
              {isLoading ? "Clearing..." : isDeletePlan ? "Delete" : "Clear All"}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Export Modal Component
  function ExportModal() {
    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(exportData)
        alert("✅ Trip data copied to clipboard!")
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea")
        textArea.value = exportData
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
        alert("✅ Trip data copied to clipboard!")
      }
    }

    return (
      <div className="modal-overlay">
        <div className="modal export-modal">
          <div className="modal-header">
            <h3>Export Trip Data</h3>
            <button onClick={() => setShowExportModal(false)} className="close-btn">
              <X size={20} />
            </button>
          </div>
          <div className="modal-content export-content">
            <textarea value={exportData} readOnly className="export-textarea" rows={15} />
          </div>
          <div className="modal-footer">
            <button onClick={() => setShowExportModal(false)} className="btn btn-secondary">
              Close
            </button>
            <button onClick={copyToClipboard} className="btn btn-primary">
              Copy to Clipboard
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default PlanningInterface
